import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
	Badge,
	Box,
	Button,
	CollectionPreferences,
	ColumnLayout,
	Container,
	Flashbar,
	Grid,
	Header,
	Link,
	Modal,
	Multiselect,
	Pagination,
	PropertyFilter,
	PropertyFilterProps,
	SpaceBetween,
	Spinner,
	Table,
	TextFilter,
} from '@amzn/awsui-components-console';
import { PropertyFilteri18nStrings } from 'src/constants/ComponentConstants';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { v4 as uuidv4 } from 'uuid';

import { SNOW_NXT_WORKFLOW_ENDPOINT } from 'src/config';
import { TaskStatus } from 'src/constants/commonEnums';
import { WorkflowDefinition } from 'src/@types/selfServeTypes/workflowDefinition.interface';
import { TaskExecutionData } from '@amzn/snow-nxt-workflow-service-typescript-client';
import { useWorkflowData } from 'src/components/commonComponents/WorkflowTable/useWorkflowData';
import useFetchOnUserActionUsingSDS from 'src/hooks/useFetchOnUserActionUsingSDS';
import { OnClickCBRequestProps } from 'src/hooks/useFetchOnUserActionUsingSDS';
import {
	TaskRow,
	OPEN_STATUSES,
	FILTERING_PROPERTIES,
	FILTERING_OPTIONS,
	CACHE_KEY,
	CACHE_TTL_MS,
	BATCH_SIZE,
	EXECUTIONS_PAGE_SIZE,
	TASK_FETCH_CONCURRENCY,
	MAX_EXECUTION_PAGES,
	SKIPPABLE_EXECUTION_STATUSES,
	COLUMN_DEFINITIONS,
	DEFAULT_PREFERENCES,
	HELP_PANEL_CONFIG,
} from './TaskDashboardConstants';
import { toCommaSeparated } from 'src/utils/CommonUtils';
import { getDefaultStripedRows } from 'src/stores/uiPreferencesStore';
import SubTaskExecutionView from 'src/components/selfServeWorkflows/SubTaskExecution/SubTaskExecutionView';
import { useTaskOutput } from 'src/hooks/useTaskOutput';
import { useUrlSyncedState } from 'src/hooks/useUrlSyncedState';
import { logger } from 'src/utils/logger';
import { useAuthContext } from 'src/providers/AuthContext';
import { useBindleAuthorization } from 'src/hooks/useBindleAuthorization';
import {
	getBindleLockForNodeType,
	getBindleLockAuthorizationRequest,
	SNOW_INTERNAL_BINDLE,
} from 'src/authentication/bindle-utils';

type SdsCallFn = <T>(props: OnClickCBRequestProps) => Promise<T>;

interface TaskDashboardUrlState {
	workflowIds: string[];
	statuses: string[];
}

const splitCsv = (value: string | null): string[] =>
	(value || '')
		.split(',')
		.map((part) => part.trim())
		.filter(Boolean);

const parseUrlState = (search: string): TaskDashboardUrlState => {
	const params = new URLSearchParams(search);

	return {
		workflowIds: splitCsv(params.get('workflowType')),
		statuses: splitCsv(params.get('status')),
	};
};

const buildUrl = (pathname: string, next: TaskDashboardUrlState) => {
	const params = new URLSearchParams();

	if (next.workflowIds.length) {
		params.set('workflowType', next.workflowIds.join(','));
	}

	if (next.statuses.length) {
		params.set('status', next.statuses.join(','));
	}

	const qs = params.toString();

	return qs ? `${pathname}?${qs}` : pathname;
};

const statusesToQuery = (statuses: string[]): PropertyFilterProps.Query => ({
	tokens: statuses.slice(0, 1).map((value) => ({
		propertyKey: 'status',
		value,
		operator: '=',
	})),
	operation: 'and',
});

const queryToStatuses = (query: PropertyFilterProps.Query): string[] => {
	const statusTokens = query.tokens.filter(
		(token) => token.propertyKey === 'status' && token.operator === '='
	);

	return statusTokens.length
		? [String(statusTokens[statusTokens.length - 1].value)]
		: [];
};

interface WorkflowExecution {
	workflowExecutionId: string;
	entityType?: string;
	startedBy?: string;
	status?: string;
}

interface WorkflowExecutionsResponse {
	workflowExecutions?: WorkflowExecution[];
	continuationToken?: string;
}

interface WorkflowScanResult {
	rows: TaskRow[];
	failedExecutions: number;
	truncated: boolean;
}

interface TaskExecutionsResponse {
	taskExecutions?: TaskExecutionData[];
}

interface CacheEntry {
	data: TaskRow[];
	timestamp: number;
}

function readCache(): CacheEntry | null {
	try {
		const raw = sessionStorage.getItem(CACHE_KEY);

		if (!raw) return null;
		const entry = JSON.parse(raw);

		if (Date.now() - entry.timestamp > CACHE_TTL_MS) {
			sessionStorage.removeItem(CACHE_KEY);

			return null;
		}

		return { data: entry.data, timestamp: entry.timestamp };
	} catch {
		return null;
	}
}

function writeCache(data: TaskRow[]): void {
	try {
		sessionStorage.setItem(
			CACHE_KEY,
			JSON.stringify({ data, timestamp: Date.now() })
		);
	} catch (e) {
		logger.warn(
			'Failed to write task dashboard cache to sessionStorage',
			e instanceof Error ? e : new Error(String(e))
		);
	}
}

async function fetchAllExecutions(
	wf: WorkflowDefinition,
	sdsCall: SdsCallFn
): Promise<{ executions: WorkflowExecution[]; truncated: boolean }> {
	const allExecutions: WorkflowExecution[] = [];
	let continuationToken: string | undefined;
	let pages = 0;
	let truncated = false;

	do {
		const queryParams: Record<string, unknown> = {
			requestId: uuidv4(),
			count: EXECUTIONS_PAGE_SIZE,
		};

		if (continuationToken) {
			queryParams.continuationToken = continuationToken;
		}

		const res = await sdsCall<WorkflowExecutionsResponse>({
			path: `/workflows/${wf.workflowId}/executions`,
			queryParams,
			method: 'GET',
			url: SNOW_NXT_WORKFLOW_ENDPOINT,
		});

		allExecutions.push(...(res?.workflowExecutions || []));
		continuationToken = res?.continuationToken || undefined;
		pages += 1;

		if (continuationToken && pages >= MAX_EXECUTION_PAGES) {
			logger.warn(
				`Stopped paginating executions at the page ceiling [workflowId=${wf.workflowId}, pages=${pages}]`
			);
			truncated = true;
			break;
		}
	} while (continuationToken);

	return { executions: allExecutions, truncated };
}

async function fetchOpenTasksForExecution(
	wf: WorkflowDefinition,
	exec: WorkflowExecution,
	sdsCall: SdsCallFn
): Promise<{ rows: TaskRow[]; failed: boolean }> {
	try {
		const taskRes = await sdsCall<TaskExecutionsResponse>({
			path: `/workflows/${wf.workflowId}/executions/${exec.workflowExecutionId}/tasks`,
			queryParams: { requestId: uuidv4() },
			method: 'GET',
			url: SNOW_NXT_WORKFLOW_ENDPOINT,
		});

		const rows = (taskRes?.taskExecutions || [])
			.filter((t) => t.status && OPEN_STATUSES.includes(t.status as TaskStatus))
			.map(
				(task) =>
					({
						workflowName: wf.workflowDefinitionName,
						workflowId: wf.workflowId,
						workflowExecutionId: exec.workflowExecutionId,
						nodes: toCommaSeparated(task.entities),
						entityType: exec.entityType || wf.entityType || '',
						taskName: task.taskName || '',
						taskType: task.taskType || '',
						taskExecutionId: task.taskExecutionId,
						status: task.status as TaskStatus,
						lastUpdateTime: task.lastUpdateTime || 0,
						startTime: task.startTime || 0,
						startedBy: exec.startedBy || '',
						comment: task.comment || '',
						attempt: task.attempt || 0,
						childWorkflowId: task.childWorkflowId,
						childWorkflowExecutionId: task.childWorkflowExecutionId,
					} as TaskRow)
			);

		return { rows, failed: false };
	} catch (e) {
		logger.warn(
			`Failed to fetch tasks for execution [workflowId=${wf.workflowId}, executionId=${exec.workflowExecutionId}]`,
			e instanceof Error ? e : new Error(String(e))
		);

		return { rows: [], failed: true };
	}
}

function needsTaskLookup(exec: WorkflowExecution): boolean {
	const status = (exec.status || '').toUpperCase();

	if (!status) return true;

	return !SKIPPABLE_EXECUTION_STATUSES.has(status);
}

async function fetchTasksForWorkflow(
	wf: WorkflowDefinition,
	sdsCall: SdsCallFn
): Promise<WorkflowScanResult> {
	let executions: WorkflowExecution[] = [];
	let truncated = false;

	try {
		const executionsResult = await fetchAllExecutions(wf, sdsCall);

		executions = executionsResult.executions;
		truncated = executionsResult.truncated;
	} catch (e) {
		logger.warn(
			`Failed to list executions [workflowId=${wf.workflowId}]`,
			e instanceof Error ? e : new Error(String(e))
		);

		return { rows: [], failedExecutions: 1, truncated: false };
	}

	const candidates = executions.filter(needsTaskLookup);

	logger.info(
		`Execution scan [workflowId=${wf.workflowId}, total=${executions.length}, needingTaskLookup=${candidates.length}]`
	);

	if (candidates.length === 0) {
		return {
			rows: [],
			failedExecutions: truncated ? 1 : 0,
			truncated,
		};
	}

	const rows: TaskRow[] = [];
	let failedExecutions = truncated ? 1 : 0;

	for (let i = 0; i < candidates.length; i += TASK_FETCH_CONCURRENCY) {
		const chunk = candidates.slice(i, i + TASK_FETCH_CONCURRENCY);
		const chunkResults = await Promise.all(
			chunk.map((exec) => fetchOpenTasksForExecution(wf, exec, sdsCall))
		);

		for (const result of chunkResults) {
			rows.push(...result.rows);
			if (result.failed) failedExecutions += 1;
		}
	}

	return { rows, failedExecutions, truncated };
}

interface TaskDashboardProps {
	openTools?: () => void;
	setTools?: (config: {
		title: React.ReactNode;
		content: React.ReactNode;
		links: Array<{ href: string; text: string }>;
	}) => void;
	hideTools?: () => void;
}

export default function TaskDashboard(
	{
		openTools,
		setTools,
		hideTools,
	}: TaskDashboardProps = {} as TaskDashboardProps
) {
	const { userName } = useAuthContext();
	const location = useLocation();
	const initialUrlStateRef = React.useRef(parseUrlState(location.search));
	const initialUrlState = initialUrlStateRef.current;
	const lockId = getBindleLockForNodeType(SNOW_INTERNAL_BINDLE);
	const { isAuthorized, isCheckingPermission, permissionError } =
		useBindleAuthorization(
			userName,
			getBindleLockAuthorizationRequest([lockId])
		);

	const [tasks, setTasks] = useState<TaskRow[]>(() => readCache()?.data || []);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const [preferences, setPreferences] = useState(() => ({
		...DEFAULT_PREFERENCES,
		stripedRows: getDefaultStripedRows(),
	}));
	const [progress, setProgress] = useState({ done: 0, total: 0 });
	const [incompleteScanCount, setIncompleteScanCount] = useState(0);
	const [scanTruncated, setScanTruncated] = useState(false);
	const scanInFlightRef = React.useRef(false);
	const [lastRefreshed, setLastRefreshed] = useState<string | null>(() => {
		const cached = readCache();

		return cached ? new Date(cached.timestamp).toLocaleTimeString() : null;
	});
	const [selectedWorkflowIds, setSelectedWorkflowIds] = useState<string[]>(
		initialUrlState.workflowIds
	);
	const [selectedTask, setSelectedTask] = useState<TaskRow | null>(null);
	const [modalVisible, setModalVisible] = useState(false);
	const [freeText, setFreeText] = useState('');

	const {
		isLoading: isLoadingWorkflows,
		error: workflowError,
		allWorkflows,
	} = useWorkflowData();
	const { onClickCB: sdsCall } = useFetchOnUserActionUsingSDS();

	const {
		taskOutput,
		isLoadingTaskOutput,
		fetchTaskOutputData,
		resetTaskOutput,
	} = useTaskOutput({
		workflowId: selectedTask?.workflowId || '',
		workflowExecutionId: selectedTask?.workflowExecutionId || '',
	});

	const mountedRef = React.useRef(true);

	useEffect(() => {
		return () => {
			mountedRef.current = false;
		};
	}, []);

	useEffect(() => {
		if (setTools) {
			setTools({
				title: <h2>{HELP_PANEL_CONFIG.title}</h2>,
				content: (
					<SpaceBetween size="m">
						{HELP_PANEL_CONFIG.content.map((text, i) => (
							<Box
								key={i}
								variant="p"
								color={
									i >= HELP_PANEL_CONFIG.content.length - 2
										? 'text-body-secondary'
										: undefined
								}
							>
								{i >= HELP_PANEL_CONFIG.content.length - 2 ? (
									<em>{text}</em>
								) : (
									text
								)}
							</Box>
						))}
					</SpaceBetween>
				),
				links: HELP_PANEL_CONFIG.links,
			});
		}

		return () => {
			if (hideTools) hideTools();
		};
	}, [setTools, hideTools]);

	const activeWorkflows = useMemo(
		() => allWorkflows.filter((wf) => wf.status === 'ACTIVE'),
		[allWorkflows]
	);

	const fetchData = useCallback(
		async (bypassCache = false) => {
			if (!bypassCache) {
				const cached = readCache();

				if (cached) {
					if (mountedRef.current) {
						setTasks(cached.data);
						setLastRefreshed(new Date(cached.timestamp).toLocaleTimeString());
					}

					return;
				}
			}

			if (activeWorkflows.length === 0) return;

			if (scanInFlightRef.current) return;
			scanInFlightRef.current = true;

			setIsLoading(true);
			setError(null);
			setIncompleteScanCount(0);
			setScanTruncated(false);
			setTasks([]);
			setProgress({ done: 0, total: activeWorkflows.length });

			let totalFailures = 0;
			let anyTruncated = false;

			try {
				const allRows: TaskRow[] = [];

				for (let i = 0; i < activeWorkflows.length; i += BATCH_SIZE) {
					if (!mountedRef.current) return;

					const batch = activeWorkflows.slice(i, i + BATCH_SIZE);
					const batchResults = await Promise.all(
						batch.map((wf) =>
							fetchTasksForWorkflow(wf, sdsCall).catch(
								() =>
									({
										rows: [],
										failedExecutions: 1,
										truncated: false,
									} as WorkflowScanResult)
							)
						)
					);

					if (!mountedRef.current) return;

					for (const result of batchResults) {
						allRows.push(...result.rows);
						totalFailures += result.failedExecutions;
						if (result.truncated) anyTruncated = true;
					}

					setProgress({
						done: Math.min(i + BATCH_SIZE, activeWorkflows.length),
						total: activeWorkflows.length,
					});
					setTasks([...allRows]);
				}

				if (!mountedRef.current) return;

				setTasks(allRows);
				setIncompleteScanCount(totalFailures);
				setScanTruncated(anyTruncated);

				if (totalFailures === 0 && !anyTruncated) {
					writeCache(allRows);
				}

				setLastRefreshed(new Date().toLocaleTimeString());
			} catch (e: unknown) {
				if (!mountedRef.current) return;

				const message =
					e instanceof Error ? e.message : 'Failed to load dashboard data';

				setError(message);
			} finally {
				scanInFlightRef.current = false;
				if (mountedRef.current) {
					setIsLoading(false);
				}
			}
		},
		[activeWorkflows, sdsCall]
	);

	useEffect(() => {
		if (activeWorkflows.length > 0) fetchData(false);
	}, [activeWorkflows]);

	const workflowOptions = useMemo(() => {
		const map = new Map<string, string>();

		for (const t of tasks) {
			if (!map.has(t.workflowId)) map.set(t.workflowId, t.workflowName);
		}

		return Array.from(map.entries()).map(([value, label]) => ({
			label,
			value,
		}));
	}, [tasks]);

	const selectedWorkflows = useMemo(
		() =>
			selectedWorkflowIds.map((id) => {
				const match = workflowOptions.find((option) => option.value === id);

				if (match) return match;

				return {
					label: isLoadingWorkflows || isLoading ? 'Loading...' : id,
					value: id,
				};
			}),
		[selectedWorkflowIds, workflowOptions, isLoadingWorkflows, isLoading]
	);

	const filtered = useMemo(() => {
		let result = tasks;

		if (selectedWorkflowIds.length > 0) {
			const ids = new Set(selectedWorkflowIds);

			result = result.filter((t) => ids.has(t.workflowId));
		}

		const q = freeText.trim().toLowerCase();

		if (q) {
			result = result.filter((t) =>
				[t.workflowName, t.workflowExecutionId, t.nodes, t.taskName, t.comment]
					.filter(Boolean)
					.some((v) => String(v).toLowerCase().includes(q))
			);
		}

		return result;
	}, [tasks, selectedWorkflowIds, freeText]);

	const columnDefinitions = useMemo(
		() =>
			COLUMN_DEFINITIONS.map((col) => {
				if (col.id === 'taskName') {
					return {
						...col,
						cell: (item: TaskRow) => (
							<Button
								variant="inline-link"
								onClick={() => {
									setSelectedTask(item);
									setModalVisible(true);
									fetchTaskOutputData({
										taskName: item.taskName,
										taskId: item.taskExecutionId,
									});
								}}
							>
								{item.taskName}
							</Button>
						),
					};
				}

				return col;
			}),
		[fetchTaskOutputData]
	);

	const {
		items,
		filteredItemsCount,
		collectionProps,
		propertyFilterProps,
		paginationProps,
		actions,
	} = useCollection(filtered, {
		propertyFiltering: {
			filteringProperties: FILTERING_PROPERTIES,
			defaultQuery: statusesToQuery(initialUrlState.statuses),
			empty: (
				<Box textAlign="center" padding="l">
					No open tasks found.
				</Box>
			),
			noMatch: (
				<Box textAlign="center" padding="l">
					No tasks match your filter.
				</Box>
			),
		},
		pagination: { pageSize: preferences.pageSize },
		sorting: {
			defaultState: {
				sortingColumn: COLUMN_DEFINITIONS.find(
					(c) => c.id === 'lastUpdateTime'
				)!,
				isDescending: true,
			},
		},
	});

	const setPropertyFilteringRef = React.useRef(actions.setPropertyFiltering);

	setPropertyFilteringRef.current = actions.setPropertyFiltering;

	const handlePropertyFilterChange = useCallback(
		(event: { detail: PropertyFilterProps.Query }) => {
			const { tokens, operation } = event.detail;
			const statusTokens = tokens.filter(
				(t) => t.propertyKey === 'status' && t.operator === '='
			);
			const otherTokens = tokens.filter(
				(t) => !(t.propertyKey === 'status' && t.operator === '=')
			);
			const latestStatus = statusTokens.slice(-1);

			setPropertyFilteringRef.current({
				tokens: [...otherTokens, ...latestStatus],
				operation,
			});
		},
		[]
	);

	const { writeUrl } = useUrlSyncedState<TaskDashboardUrlState>({
		parse: parseUrlState,
		build: (next) => buildUrl(location.pathname, next),
		onExternalChange: (next) => {
			setSelectedWorkflowIds(next.workflowIds);
			setPropertyFilteringRef.current(statusesToQuery(next.statuses));
		},
	});

	const statuses = useMemo(
		() => queryToStatuses(propertyFilterProps.query),
		[propertyFilterProps.query]
	);

	useEffect(() => {
		writeUrl({ workflowIds: selectedWorkflowIds, statuses });
	}, [selectedWorkflowIds, statuses, writeUrl]);

	const summaryRows = useMemo(() => {
		if (statuses.length === 0) return filtered;
		const statusSet = new Set(statuses);

		return filtered.filter((t) => statusSet.has(t.status));
	}, [filtered, statuses]);

	const { failedCount, pendingCount, workflowCount } = useMemo(
		() => ({
			failedCount: summaryRows.filter((t) => t.status === TaskStatus.FAILED)
				.length,
			pendingCount: summaryRows.filter(
				(t) =>
					t.status === TaskStatus.PENDING_APPROVAL ||
					t.status === TaskStatus.PENDING_REVIEW ||
					t.status === TaskStatus.SCHEDULED_FOR_DEPLOYMENT
			).length,
			workflowCount: new Set(summaryRows.map((t) => t.workflowId)).size,
		}),
		[summaryRows]
	);

	if (isCheckingPermission) {
		return (
			<Box textAlign="center" padding="xl">
				<Spinner size="large" />
			</Box>
		);
	}

	if (!isAuthorized) {
		return (
			<SpaceBetween size="l">
				<Flashbar
					items={[
						{
							type: permissionError ? 'error' : 'warning',
							header: permissionError
								? 'Failed to verify permissions'
								: 'Admin Page — Access Restricted',
							content: (
								<span>
									{permissionError ? (
										'Could not verify your access. Please try again later or contact the SNOW team.'
									) : (
										<>
											This page is restricted to SNOW internal team members. If
											you are looking for a specific workflow or entity, please
											use the{' '}
											<Link href="#/entitySearch" variant="primary">
												Entity Search
											</Link>{' '}
											page.
										</>
									)}
								</span>
							),
							id: 'access-denied',
						},
					]}
				/>
			</SpaceBetween>
		);
	}

	return (
		<SpaceBetween size="l">
			{(error || workflowError || incompleteScanCount > 0) && (
				<Flashbar
					items={[
						...(error || workflowError
							? [
									{
										type: 'error' as const,
										content: error || workflowError,
										id: 'err',
									},
							  ]
							: []),
						...(incompleteScanCount > 0
							? [
									{
										type: 'warning' as const,
										header: 'Results may be incomplete',
										content: `${
											scanTruncated ? 'At least ' : ''
										}${incompleteScanCount} execution${
											incompleteScanCount === 1 ? '' : 's'
										} could not be checked, so some open tasks may be missing. Try refreshing.`,
										dismissible: true,
										onDismiss: () => {
											setIncompleteScanCount(0);
											setScanTruncated(false);
										},
										id: 'incomplete-scan',
									},
							  ]
							: []),
					]}
				/>
			)}

			{selectedTask && (
				<Modal
					size="large"
					onDismiss={() => {
						setModalVisible(false);
						setSelectedTask(null);
						resetTaskOutput();
					}}
					header={selectedTask.taskName}
					visible={modalVisible}
				>
					{modalVisible && (
						<SubTaskExecutionView
							taskDetails={{
								taskName: selectedTask.taskName,
								taskExecutionId: selectedTask.taskExecutionId,
								taskType: selectedTask.taskType,
								status: selectedTask.status,
								childWorkflowId: selectedTask.childWorkflowId,
								childWorkflowExecutionId: selectedTask.childWorkflowExecutionId,
							}}
							workflowId={selectedTask.workflowId}
							workflowExecutionId={selectedTask.workflowExecutionId}
							taskOutput={taskOutput}
							isLoadingTaskOutput={isLoadingTaskOutput}
						/>
					)}
				</Modal>
			)}

			<Table
				{...collectionProps}
				resizableColumns
				stickyHeader
				variant="full-page"
				loading={(isLoading || isLoadingWorkflows) && tasks.length === 0}
				loadingText={
					progress.total > 0
						? `Scanning workflows... (${progress.done}/${progress.total}).`
						: 'Loading...'
				}
				items={items}
				columnDefinitions={columnDefinitions}
				columnDisplay={preferences.contentDisplay}
				stripedRows={preferences.stripedRows}
				wrapLines={preferences.wrapLines}
				header={
					<SpaceBetween size="m">
						<Header
							variant="awsui-h1-sticky"
							counter={`(${filteredItemsCount})`}
							info={
								openTools && (
									<Link variant="info" onFollow={() => openTools()}>
										Info
									</Link>
								)
							}
							actions={
								<SpaceBetween direction="horizontal" size="s">
									<Button
										iconName="refresh"
										onClick={() => fetchData(true)}
										loading={isLoading}
									>
										Refresh
									</Button>
								</SpaceBetween>
							}
						>
							Task Dashboard
							{isLoading && progress.total > 0 && (
								<Box
									variant="small"
									color="text-body-secondary"
									display="inline-block"
									padding={{ left: 's' }}
								>
									Scanning: {progress.done}/{progress.total} workflows
								</Box>
							)}
							{lastRefreshed && !isLoading && (
								<Box
									variant="small"
									color="text-body-secondary"
									display="inline-block"
									padding={{ left: 's' }}
								>
									Last refreshed: {lastRefreshed}
								</Box>
							)}
						</Header>
						<Container header={<Header variant="h2">Summary</Header>}>
							<ColumnLayout columns={4} variant="text-grid">
								<SpaceBetween size="xxs">
									<Box variant="awsui-key-label">Failed Tasks</Box>
									<Box>
										<Badge color="red">{failedCount}</Badge>
									</Box>
								</SpaceBetween>
								<SpaceBetween size="xxs">
									<Box variant="awsui-key-label">Pending Action</Box>
									<Box>
										<Badge color="blue">{pendingCount}</Badge>
									</Box>
								</SpaceBetween>
								<SpaceBetween size="xxs">
									<Box variant="awsui-key-label">Running Workflows</Box>
									<Box>{workflowCount}</Box>
								</SpaceBetween>
								<SpaceBetween size="xxs">
									<Box variant="awsui-key-label">Total Open Tasks</Box>
									<Box>{summaryRows.length}</Box>
								</SpaceBetween>
							</ColumnLayout>
						</Container>
					</SpaceBetween>
				}
				pagination={<Pagination {...paginationProps} />}
				preferences={
					<CollectionPreferences
						title="Preferences"
						confirmLabel="Confirm"
						cancelLabel="Cancel"
						preferences={preferences}
						onConfirm={({ detail }) =>
							setPreferences(detail as typeof DEFAULT_PREFERENCES)
						}
						pageSizePreference={{
							title: 'Page size',
							options: [
								{ value: 10, label: '10 tasks' },
								{ value: 25, label: '25 tasks' },
								{ value: 50, label: '50 tasks' },
								{ value: 100, label: '100 tasks' },
							],
						}}
						stripedRowsPreference={{
							label: 'Striped rows',
							description: 'Alternate row shading',
						}}
						wrapLinesPreference={{
							label: 'Wrap lines',
							description: 'Wrap text content within table cells',
						}}
						contentDisplayPreference={{
							title: 'Column preferences',
							options: COLUMN_DEFINITIONS.map((col) => ({
								id: col.id,
								label: col.header,
								alwaysVisible: col.id === 'taskName',
							})),
						}}
					/>
				}
				filter={
					<Grid
						gridDefinition={[{ colspan: 3 }, { colspan: 3 }, { colspan: 6 }]}
					>
						<Multiselect
							selectedOptions={selectedWorkflows}
							onChange={({ detail }) =>
								setSelectedWorkflowIds(
									detail.selectedOptions
										.map((option) => option.value)
										.filter((value): value is string => !!value)
								)
							}
							options={workflowOptions}
							placeholder={
								isLoadingWorkflows || isLoading
									? 'Loading workflows...'
									: 'All workflows'
							}
							filteringType="auto"
							tokenLimit={1}
							statusType={
								isLoadingWorkflows || isLoading ? 'loading' : 'finished'
							}
							loadingText="Loading workflows..."
						/>
						<PropertyFilter
							{...propertyFilterProps}
							onChange={handlePropertyFilterChange}
							filteringOptions={FILTERING_OPTIONS}
							i18nStrings={{
								...PropertyFilteri18nStrings,
								groupPropertiesText: 'Filters',
								clearFiltersText: 'Clear Filters',
								filteringPlaceholder: 'Filter by status',
							}}
							hideOperations
							tokenLimit={1}
							countText={`${filteredItemsCount} ${
								filteredItemsCount === 1 ? 'match' : 'matches'
							}`}
							expandToViewport
						/>
						<TextFilter
							filteringText={freeText}
							onChange={({ detail }) => setFreeText(detail.filteringText)}
							filteringPlaceholder="Search by workflow, node, task name, or comment"
							filteringAriaLabel="Search tasks"
							countText={`${filteredItemsCount} ${
								filteredItemsCount === 1 ? 'match' : 'matches'
							}`}
						/>
					</Grid>
				}
			/>
		</SpaceBetween>
	);
}
